import { unsafeCoerce } from 'fp-ts/lib/function';
import { Dispatch, useCallback, useState, useEffect } from 'react';
import { StatePair } from '../../../../UnicronApp/preloader/StatePair';
import { TextField } from '../../../../common/components/fields/TextField/TextField';
import { useChangeEvent } from '../../../../common/hooks/useChangeEvent';
import { Wrapper } from './AutoComplete.styles';
import { AutoCompleteBody } from './AutoCompleteBody';
import { PredictionType } from './PredictionType';

export interface Props<T> {
  id?: string;
  labelKey?: string;
  placeholder?: string;
  required?: boolean;
  complete?: boolean;
  error?: boolean;
  className?: string;
  state: StatePair<string | undefined>;
  onSelectPrediction?: Dispatch<T>;
  onClear?: Dispatch<string>;
  autoCompleteHook: (value?: string) => T[];
  disabled?: boolean;
  autoComplete?: string;
  allowCustom?: boolean;
}

export const AutoCompleteField = <T extends PredictionType>({
  className,
  id,
  state,
  onSelectPrediction,
  onClear,
  autoCompleteHook,
  allowCustom,
  ...textFieldProps
}: Props<T>) => {
  const [focused, setFocused] = useState(false);
  const [focusKey, setFocusKey] = useState(1);
  const [search, setSearch] = useState<string | undefined>();
  const [value, setValue] = state;

  const autoCompleteHandler = (value?: string): T[] => {
    const results = autoCompleteHook(value);
    const baseCase: T = unsafeCoerce({ value: value ?? '', label: value ?? '' });
    return allowCustom ? [...results, baseCase] : results;
  };

  const handleChange = useChangeEvent(value => {
    setSearch(value);

    if (value?.length === 0) {
      onClear?.(value);
    }
  });

  useEffect(() => setSearch(value), [value]);

  const selectDropDown = (selectedPrediction: T) => {
    setValue(selectedPrediction.value);
    onSelectPrediction?.(selectedPrediction);
    handleBlur(selectedPrediction.value);
  };

  const handleFocus = useCallback(() => setFocused(true), []);

  const handleBlur = (value?: string) => {
    setSearch(value);
    setFocused(false);
    setFocusKey(key => key + 1);
  };

  return (
    <Wrapper className={className}>
      <TextField
        id={id}
        key={focusKey}
        onChange={handleChange}
        value={search || ''}
        autoComplete="off"
        onFocus={handleFocus}
        onBlur={() => handleBlur()}
        {...textFieldProps}
      />

      {focused && (
        <AutoCompleteBody
          autoCompleteHook={autoCompleteHandler}
          value={search}
          onSelect={selectDropDown}
          cancel={handleBlur}
        />
      )}
    </Wrapper>
  );
};
